var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { AnalyticsBrowser } from '@segment/analytics-next';
import camelCase from 'lodash.camelcase';
/**
 * Converts the keys of an object from kebab case to camel case if they are in kebab case.
 *
 * @param {{ [key: string]: any }} obj - The object with potentially kebab-cased keys.
 * @returns A new object with camel-cased keys where applicable.
 */
function convertObjectKeysToCamelCase(obj) {
    var newObj = {};
    for (var key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            var newKey = key.includes('-') ? camelCase(key) : key;
            newObj[newKey] = obj[key];
        }
    }
    return newObj;
}
var WebAnalytics = /** @class */ (function () {
    function WebAnalytics(_a) {
        var writeKey = _a.writeKey;
        this.analyticsInstance = AnalyticsBrowser.load({
            writeKey: writeKey,
        });
    }
    /**
     * Track an event.
     * Lets you record the actions your users perform. Every action triggers what we call an “event”, which can also have associated properties.
     *
     * @template T - The type of the event, extending BaseEvent.
     * @param {T} event - The event to track, must be of type T.
     * @returns Promise
     * @example
     * analytics.trackEvent({
     *  category: EventCategory.DASHBOARD,
     *  event_name: 'Button Clicked',
     *  buttonType: ButtonType.PRIMARY,
     *  position: productLable,
     *  customerType: CustomerType.CUSTOMER,
     *  text: capitalizeString(actionLabel),
     * })
     *
     */
    WebAnalytics.prototype.trackEvent = function (event) {
        // eslint-disable-next-line camelcase
        var event_name = event.event_name, otherProperties = __rest(event, ["event_name"]);
        // Add path property to otherProperties based on current location
        otherProperties.path = location.href.replace(location.origin, '');
        return this.analyticsInstance.track(event_name, otherProperties);
    };
    /**
     * Identify a user with the provided user ID and optional traits.
     * Lets you tie a user to their actions and record traits about them. It includes a unique User ID and/or anonymous ID, and any optional traits you know about them.
     * Call identify once when the user’s account is first created, and then again any time their traits change.
     *
     * @param {string} userId - The unique identifier for the user.
     * @param {{ [key: string]: any }} featureFlags - LaunchDarkly feature flags associated with the user.
     * @param {Object} [userTraits] - Optional traits associated with the user.
     * @param {Object} [options] - Additional options for the identification process.
     * @returns  - Promise
     *
     * @example
     * // Identify a user with basic information
     * analyticsTracker.identify("user123", { featureFlag: true }, { name: "John Doe", email: "john@example.com" });
     *
     * // Identify a user with options
     * analyticsTracker.identify("user123", { featureFlag: true }, { name: "John Doe" }, { timestamp: 1637731200000 });
     */
    WebAnalytics.prototype.identify = function (userId, featureFlags, userTraits, options) {
        var traits = __assign(__assign({}, userTraits), { test_experiment_flags: convertObjectKeysToCamelCase(featureFlags) });
        return this.analyticsInstance.identify(userId, traits, options);
    };
    /**
     * Track a page view with the specified category and name.
     * Lets you record page views on your website, along with optional extra information about the page being viewed.
     *
     * @param {EventCategory} category - The category of the page event.
     * @param {string} name - The name or title of the page.
     * @param {Object} [properties] - Optional properties associated with the page view.
     * @returns - Promise
     *
     * @example
     * // Track a page view with category and name
     * analyticsTracker.trackPage("Home", "Homepage");
     */
    WebAnalytics.prototype.trackPage = function (category, name, properties) {
        var _a;
        var isAuthenticated;
        try {
            var auth = JSON.parse(window.sessionStorage.getItem('lendi_auth') || '{}');
            isAuthenticated = !!((_a = auth.identity) === null || _a === void 0 ? void 0 : _a.id);
        }
        catch (error) {
            isAuthenticated = false;
        }
        return this.analyticsInstance.page(category, name, __assign(__assign({}, properties), { isAuthenticated: isAuthenticated }));
    };
    /**
     * Identify the specific group or company, the group method receives traits that are specific to the group.
     *
     * @param {string} groupId - The unique identifier for the group.
     * @param {Object} [traits] - Optional traits associated with the group.
     * @param {Object} [options] - Free-form dictionary of traits of the group, like email or name
     * @returns  - Promise
     *
     * @example
     * // Identify a group with basic information
     * analyticsTracker.group("group123", { name: "Infitech", industry: "Technology", employees: 329, plan: "enterprise", "total billed": 830 });
     *
     */
    WebAnalytics.prototype.trackGroup = function (groupId, traits, options) {
        return this.analyticsInstance.group(groupId, traits, options);
    };
    return WebAnalytics;
}());
export default WebAnalytics;
//# sourceMappingURL=index.js.map